import { Injectable } from '@angular/core';
import { Event as RouterEvent, NavigationError, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import {
  clearRecoveryAttemptsIfHealthy,
  isModuleLoadFailure,
  recoverFromModuleLoadFailure
} from '../../utils/module-load-failure.util';
import { SpotlitePreloadStrategy } from '../../utils/spotlite-preload.strategy';

/**
 * Turns a failed lazy-route chunk import into a one-shot reload instead of a dead app.
 *
 * See `module-load-failure.util.ts` for why a reload — not a retry — is the only
 * cure: WebKit caches the failed `import()` in the document's module map, so every
 * subsequent attempt at that route replays the same rejection with no network
 * request. Before this, a guest who hit one bad chunk fetch on the way to
 * `/manage-reservation` saw a permanently blank page until they force-quit Safari
 * (Sentry SPOTLITE-C8).
 *
 * Reload attempts are capped per session, so a genuinely unhealthy origin surfaces
 * its error rather than putting the guest in a reload loop.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  constructor(
    private readonly router: Router,
    private readonly preloadStrategy: SpotlitePreloadStrategy
  ) {}

  init(): void {
    this.router.events
      .pipe(filter((event: RouterEvent): event is NavigationError => event instanceof NavigationError))
      .subscribe(event => this.onNavigationError(event));

    // Reset the counter once the app has stayed healthy for a while, so a guest who
    // recovered early in a long session can still recover later on.
    //
    // The reset is time-gated inside clearRecoveryAttemptsIfHealthy, NOT granted by
    // the navigation itself: the router routinely completes a navigation and then an
    // Ionic component chunk fails seconds later through the unhandledrejection path
    // below. Resetting on NavigationEnd alone would refill the attempt budget on
    // every reload and loop forever.
    this.router.events
      .pipe(filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => clearRecoveryAttemptsIfHealthy());

    // Ionic/Stencil lazily imports its own component chunks outside the router, so
    // those failures arrive as unhandled rejections rather than NavigationError.
    // They are what produce "Constructor for ion-app#undefined was not found".
    globalThis.addEventListener?.('unhandledrejection', (event: PromiseRejectionEvent) => {
      if (isModuleLoadFailure(event.reason)) {
        void recoverFromModuleLoadFailure();
      }
    });
  }

  private onNavigationError(event: NavigationError): void {
    // The full path, not just its first segment: routes like `giftcard/purchase`
    // and `tee-time/booking` are recorded by the preload strategy under their whole
    // pattern, and matching on `giftcard` alone would never find them.
    const poisonedByPreload = this.preloadStrategy.hasFailedPath(event.url);

    if (!isModuleLoadFailure(event.error) && !poisonedByPreload) {
      return;
    }

    void recoverFromModuleLoadFailure(event.url);
  }
}
